/**
 * Resource Field Filter
 * 
 * Filters out resource selector fields from config schemas
 * so they don't appear in the generated forms.
 * 
 * These fields are instead shown in the Step Editor Panel's
 * Resources section for better UX.
 */

/**
 * Check if a schema field is a resource selector
 */
export function isResourceField(schema: any, fieldName?: string): boolean {
  if (!schema || typeof schema !== 'object') {
    return false;
  }

  const format = schema.format;
  const name = fieldName?.toLowerCase() || '';

  // Check by format
  if (format) {
    const resourceFormats = [
      'productSelector',
      'variantSelector',
      'priceSelector',
      'offerSelector',
      'orderBumpSelector',
    ];

    if (resourceFormats.includes(format)) {
      return true;
    }
  }

  // Check by field name patterns
  if (
    name.includes('productid') ||
    name.includes('variantid') ||
    name.includes('priceid') ||
    name.includes('offerid') ||
    name.includes('orderbumpid') ||
    name.includes('product_id') ||
    name.includes('variant_id') ||
    name.includes('price_id') ||
    name.includes('offer_id') ||
    name.includes('orderbump_id')
  ) {
    return true;
  }

  return false;
}

/**
 * Filter resource fields from a schema
 * Returns a new schema without resource fields
 */
export function filterResourceFieldsFromSchema(schema: any): any {
  if (!schema || typeof schema !== 'object') {
    return schema;
  }

  // If this is an array schema, process items
  if (schema.type === 'array' && schema.items) {
    return {
      ...schema,
      items: filterResourceFieldsFromSchema(schema.items),
    };
  }

  // If this has properties (object schema)
  if (schema.properties) {
    const filteredProperties: any = {};
    const filteredRequired: string[] = [];

    Object.entries(schema.properties).forEach(([key, propSchema]) => {
      // Skip resource fields
      if (!isResourceField(propSchema, key)) {
        filteredProperties[key] = filterResourceFieldsFromSchema(propSchema);
        
        // Keep in required array if it was required
        if (schema.required?.includes(key)) {
          filteredRequired.push(key);
        }
      }
    });

    return {
      ...schema,
      properties: filteredProperties,
      ...(filteredRequired.length > 0 && { required: filteredRequired }),
    };
  }

  return schema;
}

/**
 * Check if a schema has any resource fields
 */
export function hasResourceFields(schema: any, path: string[] = []): boolean {
  if (!schema || typeof schema !== 'object') {
    return false;
  }

  const fieldName = path[path.length - 1] || '';

  // Check current field
  if (isResourceField(schema, fieldName)) {
    return true;
  }

  // Check properties
  if (schema.properties) {
    return Object.entries(schema.properties).some(([key, propSchema]) =>
      hasResourceFields(propSchema, [...path, key])
    );
  }

  // Check array items
  if (schema.items) {
    return hasResourceFields(schema.items, [...path, 'items']);
  }

  return false;
}

